import { observer } from 'mobx-react';
import React, { useEffect, useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';
import PhoneInput2 from 'react-phone-input-2';
import { StaticContext } from 'react-router';
import { RouteComponentProps } from 'react-router-dom';
import { BUTTON_TITLE, PER_PAGE } from 'src/constants/main';
import { SCREENS } from 'src/constants/screens';
import { countriesStore } from 'src/mobx/countriesStore';
import { localeStore } from 'src/mobx/localesStore';
import { userStore } from 'src/mobx/userStore';
import { BRANCHES_MODAL_FETCH_PARAMETER } from 'src/types/branches';
import { LoadingStatus } from 'src/types/main';
import { IV4UserBranch, IV4UserFranchise, IV4UserRole } from 'src/types/user';
import { leaveWithoutSaving } from 'src/utils/modalConfirmations';
import Input from 'src/view/components/Controls/Input/Input';
import Picker from 'src/view/components/Controls/Pickers/Picker/Picker';
import { IPickerOption } from 'src/view/components/Controls/Pickers/Picker/types';
import Switcher from 'src/view/components/Controls/Switcher/Switcer';
import Text, { TEXT } from 'src/view/components/Text/Text';
import HeaderBack from 'src/view/containers/Headers/components/HeaderBack/HeaderBack';
import Headers from 'src/view/containers/Headers/Headers';
import { IBranchesModalItem } from 'src/view/containers/Modals/BranchesModal/BranchesModal';
import BranchesModalOpen from 'src/view/containers/Modals/BranchesModal/components/BranchesModalOpen/BranchesModalOpen';
import { IDefaultUserFieldValidation } from 'src/view/screens/SettingsScreen/screens/General/ManageUsers/screens/AllUsers/types/users';
import franchisesStore from 'src/view/screens/SettingsScreen/store/franchisesStore';
import rolesStore from 'src/view/screens/SettingsScreen/store/rolesStore';
import usersStore from 'src/view/screens/SettingsScreen/store/usersStore';
import { appStore } from '~/mobx/appStore';
import Button from '~/view/components/Controls/ButtonComponent/Button';
import './style.scss';
import ScrollableBlock from 'src/view/containers/ScrollableBlock/ScrollableBlock';
import { USER_ROLES } from '~/constants/user';

type TProps = RouteComponentProps<{ id?: string }, StaticContext, undefined>;

const EditUser: React.FC<TProps> = observer((props: TProps): JSX.Element => {
  useEffect(() => {
    if (!rolesStore.roles.length) {
      rolesStore.getRoles();
    }
    franchisesStore.changePerPage(PER_PAGE.MAXIMUM);
    franchisesStore.getFranchises();
    countriesStore.fetchCountries();
    if (props.match.params.id) {
      usersStore.setUser(usersStore.users.find((user) => user.id === Number(props.match.params.id)));
      if (!usersStore.user?.id) usersStore.getUser(props.match.params.id);
      return;
    }
    usersStore.setUser();
  }, []);

  useEffect(() => {
    if (!usersStore.user?.id || usersStore.userCreateLoadingStatus !== LoadingStatus.Success) {
      return;
    }
    props.history.replace(SCREENS.SETTINGS_USERS());
  }, [usersStore.userCreateLoadingStatus]);

  useEffect(() => {
    if (!usersStore.user?.id || usersStore.user?.id === 0 || usersStore.userUpdateLoadingStatus !== LoadingStatus.Success) {
      return;
    }
    props.history.replace(SCREENS.SETTINGS_USERS());
  }, [usersStore.userUpdateLoadingStatus]);

  const onPhoneChange = (phone: string = '') => {
    usersStore.changeUserHandler({
      mobile: Number(phone),
    });
  };

  const selectedRoles = useMemo((): IPickerOption[] => {
    const allRoles = rolesStore.roles;
    return (usersStore.user?.roles || []).map((role: IV4UserRole) => {
      const selectedRoleName = allRoles.find((_role) => _role.id === role.id)?.name;
      return {
        value: role.id,
        label: localeStore.t(`${selectedRoleName || ''}_role`),
      } as IPickerOption;
    });
  }, [usersStore.user?.roles]);

  const selectRole = (options: IPickerOption[]) => {
    usersStore.changeUserHandler({
      roles: options.map((option) => {
        return {
          id: option.value,
          name: option.label,
        } as IV4UserRole;
      }),
    });
  };

  const selectedFranchises = useMemo((): IPickerOption[] => {
    return (usersStore.user?.franchises || []).map((franchise: IV4UserFranchise) => {
      return {
        value: franchise.id,
        label: franchise.title,
      } as IPickerOption;
    });
  }, [usersStore.user?.franchises, usersStore.user?.roles]);

  const selectFranchise = (options: IPickerOption[]) => {
    usersStore.changeUserHandler({
      franchises: options.map((option) => {
        return {
          id: option.value,
          title: option.label,
        } as IV4UserFranchise;
      }),
    });
  };

  const selectBranches = (branches: IBranchesModalItem[]) => {
    usersStore.changeUserHandler({
      branches: branches.map((branch) => {
        return {
          id: branch.id,
          name: branch.name,
        } as IV4UserBranch;
      }),
    });
  };

  const isLoading = useMemo((): boolean => {
    const loadingStatuses = [LoadingStatus.Loading, LoadingStatus.Init, LoadingStatus.Refreshing];
    return (
      loadingStatuses.includes(usersStore.userLoadingStatus) ||
      loadingStatuses.includes(rolesStore.rolesLoadingStatus) ||
      loadingStatuses.includes(franchisesStore.franchisesLoadingStatus)
    );
  }, [usersStore.userLoadingStatus, rolesStore.rolesLoadingStatus, franchisesStore.franchisesLoadingStatus]);

  const hasErrors = useMemo((): boolean => {
    return Object.values(usersStore.validation).filter((value: IDefaultUserFieldValidation) => !value.state).length > 0;
  }, [usersStore.validation]);

  const branchesPlaceholder = (): string => {
    if (usersStore.isUserRestaurantManager) {
      return localeStore.t('all_branches');
    }
    if (usersStore.isUserFranchise) {
      return localeStore.t('all_branches_in_franchise');
    }
    return '';
  };

  const franchisesPlaceholder = (): string => {
    if (usersStore.isUserRestaurantManager) {
      return localeStore.t('all_franchises');
    }
    if (usersStore.isUserFranchise) {
      return localeStore.t('select_franchise');
    }
    return `0 ${localeStore.t('selected')}`;
  };

  const goBack = () => {
    if (!usersStore.edited) {
      props.history.push(SCREENS.SETTINGS_USERS());
      return;
    }
    leaveWithoutSaving({
      title: localeStore.t('title_leave_without_changes'),
      message: localeStore.t('leave_without_changes_desc'),
      onConfirm: () => props.history.push(SCREENS.SETTINGS_USERS()),
    });
  };

  const onSave = () => {
    if (usersStore.user?.id) {
      usersStore.updateUser();
      return;
    }
    usersStore.createUser();
  };

  const isFranchiseEnabled = useMemo((): boolean => appStore.isAddonsAvailable.franchise, [appStore.isAddonsAvailable.franchise]);

  if (isFranchiseEnabled === false) {
    rolesStore.setHideRoles([USER_ROLES.FRANCHISEE]);
  }

  return (
    <div className="d-flex flex-column full-width edit-user-page">
      <Headers>
        {!isLoading ? (
          <div>
            <HeaderBack title={`${usersStore.user?.name || ''}`.trim()} callback={goBack} path={SCREENS.SETTINGS_USERS()} />
          </div>
        ) : (
          <Skeleton width={400} height={30} style={{ borderRadius: 12 }} />
        )}
      </Headers>
      <ScrollableBlock>
        <div className="d-flex flex-column flex-grow-1 pt-6">
          {/* user status */}
          {(!props.match.params.id || userStore.user.id !== Number(props.match.params.id)) && (
            <div className="d-flex flex-row align-items-center mb-3">
              {!isLoading ? (
                <Switcher status={usersStore.user?.status || 0} onChange={(status) => usersStore.changeUserHandler({ status })} />
              ) : (
                <Skeleton width={50} height={46} style={{ borderRadius: 12 }} />
              )}
              <Text type={TEXT.CUSTOM} text="status" className="edit-user-page__switcher-label" />
              {!usersStore.validation.status.state && usersStore.validation.status.errorText ? (
                <div className="input-error-message ml-3">{usersStore.validation.status.errorText}</div>
              ) : null}
            </div>
          )}
          {/* user role */}
          <div className="d-flex align-items-center edit-user-page__input-wrapper">
            <Text
              type={TEXT.CUSTOM}
              text="user_role"
              className={`edit-user-page__input-label ${!usersStore.validation.role.state ? 'mb-6' : ''}`}
              required
            />
            {!isLoading ? (
              <div>
                <Picker
                  options={rolesStore.selectRolesOptions}
                  selectedOptions={selectedRoles}
                  onSelect={selectRole}
                  placeholder={localeStore.t('select_user_role')}
                  className={`edit-user-page__select ${!usersStore.validation.role.state ? 'input-error' : ''}`}
                />
                {!usersStore.validation.role.state && usersStore.validation.role.errorText ? (
                  <div className="input-error-message">{usersStore.validation.role.errorText}</div>
                ) : null}
              </div>
            ) : (
              <Skeleton width={240} height={46} style={{ borderRadius: 12 }} />
            )}
          </div>
          {/* user franchisee */}
          {isFranchiseEnabled && (
            <div className="d-flex align-items-center edit-user-page__input-wrapper">
              <Text
                type={TEXT.CUSTOM}
                text="select_franchise"
                className={`edit-user-page__input-label ${!usersStore.validation.franchises.state ? 'mb-6' : ''}`}
              />
              {!isLoading ? (
                <div>
                  <Picker
                    options={franchisesStore.selectFranchisesOptions}
                    selectedOptions={selectedFranchises}
                    onSelect={selectFranchise}
                    placeholder={franchisesPlaceholder()}
                    className={`edit-user-page__select ${!usersStore.validation.franchises.state ? 'input-error' : ''}`}
                    isMulti
                    disabled={!usersStore.isUserFranchise}
                  />
                  {!usersStore.validation.franchises.state && usersStore.validation.franchises.errorText ? (
                    <div className="input-error-message">{usersStore.validation.franchises.errorText}</div>
                  ) : null}
                </div>
              ) : (
                <Skeleton width={240} height={46} style={{ borderRadius: 12 }} />
              )}
            </div>
          )}
          {/* user branch */}
          <div className="d-flex align-items-center edit-user-page__input-wrapper">
            <Text
              type={TEXT.CUSTOM}
              text="select_branch"
              className={`edit-user-page__input-label ${!usersStore.validation.branches.state ? 'mb-6' : ''}`}
              required
            />
            {!isLoading ? (
              <div>
                {!usersStore.isUserRestaurantManager && !usersStore.isUserFranchise ? (
                  <BranchesModalOpen
                    titleClassName="title"
                    className={`edit-user-page__select ${!usersStore.validation.branches.state ? 'input-error' : ''}`}
                    selectedBranches={usersStore.user?.branches || []}
                    onSave={selectBranches}
                    highlightUnavailable={false}
                    fetchParamDelete={BRANCHES_MODAL_FETCH_PARAMETER.ALL}
                    isMulti
                  />
                ) : (
                  <Picker
                    options={[]}
                    selectedOptions={[]}
                    onSelect={() => undefined}
                    placeholder={branchesPlaceholder()}
                    className="edit-user-page__select"
                    isMulti
                    disabled
                  />
                )}
                {!usersStore.validation.branches.state && usersStore.validation.branches.errorText ? (
                  <div className="input-error-message">{usersStore.validation.branches.errorText}</div>
                ) : null}
              </div>
            ) : (
              <Skeleton width={240} height={46} style={{ borderRadius: 12 }} />
            )}
          </div>
          {/* user email */}
          <div className="d-flex align-items-center edit-user-page__input-wrapper">
            <Text
              type={TEXT.CUSTOM}
              text="hint_email_id"
              className={`edit-user-page__input-label ${!usersStore.validation.email.state ? 'mb-6' : ''}`}
              required
            />
            {!isLoading ? (
              <Input
                wrapperClassName="edit-user-page__input"
                error={!usersStore.validation.email.state}
                onChange={(e) => usersStore.changeUserHandler({ email: e.target.value })}
                placeholder={localeStore.t('hint_email_id')}
                value={usersStore.user?.email}
                className="input-mini family-medium text-elipsis"
                errorMessage={usersStore.validation.email.errorText || localeStore.t('toast_email_invalid')}
              />
            ) : (
              <Skeleton width={240} height={46} style={{ borderRadius: 12 }} />
            )}
          </div>
          {/* user username */}
          <div className="d-flex align-items-center edit-user-page__input-wrapper">
            <Text
              type={TEXT.CUSTOM}
              text="name"
              className={`edit-user-page__input-label ${!usersStore.validation.name.state ? 'mb-6' : ''}`}
              required
            />
            {!isLoading ? (
              <Input
                wrapperClassName="edit-user-page__input"
                error={!usersStore.validation.name.state}
                onChange={(e) => usersStore.changeUserName(e.target.value)}
                placeholder={localeStore.t('name')}
                value={usersStore.user?.name}
                className="input-mini family-medium text-elipsis"
                errorMessage={usersStore.validation.name.errorText}
              />
            ) : (
              <Skeleton width={240} height={46} style={{ borderRadius: 12 }} />
            )}
          </div>
          {/* user password */}
          <div className="d-flex align-items-center edit-user-page__input-wrapper">
            <Text
              type={TEXT.CUSTOM}
              text="hint_password"
              className={`edit-user-page__input-label ${!usersStore.validation.password.state ? 'mb-6' : ''}`}
              required
            />
            {!isLoading ? (
              <Input
                wrapperClassName="edit-user-page__input"
                error={!usersStore.validation.password.state}
                onChange={(e) => usersStore.changeUserHandler({ password: e.target.value })}
                placeholder={localeStore.t('password_placeholder')}
                value={usersStore.user?.password}
                className="input-mini family-medium text-elipsis"
                errorMessage={usersStore.validation.password.errorText}
              />
            ) : (
              <Skeleton width={240} height={46} style={{ borderRadius: 12 }} />
            )}
          </div>
          {/* user username */}
          <div className="d-flex align-items-center edit-user-page__input-wrapper">
            <Text
              type={TEXT.CUSTOM}
              text="txt_login"
              className={`edit-user-page__input-label ${!usersStore.validation.username.state ? 'mb-6' : ''}`}
              required
            />
            {!isLoading ? (
              <Input
                wrapperClassName="edit-user-page__input"
                error={!usersStore.validation.username.state}
                onChange={(e) => usersStore.changeUserHandler({ username: e.target.value })}
                placeholder={localeStore.t('txt_login')}
                value={usersStore.user?.username}
                className="input-mini family-medium text-elipsis"
                errorMessage={usersStore.validation.username.errorText}
              />
            ) : (
              <Skeleton width={240} height={46} style={{ borderRadius: 12 }} />
            )}
          </div>
          {/* user mobile */}
          <div className="d-flex align-items-center edit-user-page__input-wrapper mb-0">
            <Text
              type={TEXT.CUSTOM}
              text="txt_mobile"
              className={`edit-user-page__input-label ${!usersStore.validation.mobile.state ? 'mb-6' : ''}`}
              required
            />
            {!isLoading ? (
              <div dir="ltr">
                <PhoneInput2
                  country={
                    !usersStore.user || usersStore.user?.mobile === null || usersStore.user?.mobile === undefined
                      ? countriesStore.defaultCountryCode
                      : undefined
                  }
                  containerClass={`input input-mini edit-user-page__input ${!usersStore.validation.mobile.state ? 'input-error' : ''}`}
                  prefix="+"
                  value={`${usersStore.user?.mobile || ''}`}
                  onChange={onPhoneChange}
                  defaultErrorMessage={usersStore.validation.mobile.errorText}
                  inputClass="w-100"
                />
              </div>
            ) : (
              <Skeleton width={240} height={46} style={{ borderRadius: 12 }} />
            )}
          </div>
          <Button title={localeStore.t(BUTTON_TITLE.SAVE)} onClick={onSave} disabled={hasErrors} loading={isLoading} className="mt-8" />
        </div>
      </ScrollableBlock>
    </div>
  );
});

export default EditUser;
